/**
 * Header nav shape — the ONE place the new header's IA is declared.
 *
 * Everything is keyed by **slug**, never by WordPress term id: term ids are per-install
 * (dev and prod differ), and a wrong id returns a plausible count for the wrong term,
 * which is the one failure this whole layer exists to prevent. `wp.ts` resolves
 * slug → id at fetch time and caches the mapping.
 *
 * Both the server (assembling the document) and the client (rendering the nav before /
 * without the document) import this, so the nav can never disagree with its own data.
 */

import {
  EDITORIAL_HUBS,
  FALLBACK_TREE,
  hubRootHref,
  type EditorialHub,
  type EditorialScope,
  type EditorialTree,
} from 'lib/editorial/taxonomy'

/**
 * A header tab: one editorial scope with a DealHunt catalogue node — half Browse
 * (catalogue), half Coverage (the hubs scoped to it). The plain-data slice of the scope
 * the header chrome needs, small enough to travel in every page's common data.
 */
export interface HeaderTab {
  id: string
  label: string
  /** Lowercase noun for prose: "View all 378 earbuds". */
  short: string
  dealHuntSlug: string
  /** The hubs the scope has pages on, null = every hub (`EditorialScope.hubs`). The
   *  Coverage rows are filtered by it: a hub WordPress took off the scope is a 404. */
  hubs: readonly string[] | null
}

/**
 * The header's tabs ARE the editorial scopes that have a catalogue node — same ids, same
 * topic unions, read from the SAME loaded tree as the coverage pages
 * (`lib/editorial/tree`), because the Coverage count in a panel and the page it opens
 * must mean the same thing by "Earbuds". An editorial-only scope (microphones…) has no
 * Browse half and is not a tab. WordPress sets `dealhunt_slug` per scope (v6: header-only).
 */
export function headerTabs(tree: EditorialTree): HeaderTab[] {
  return tree.scopes
    .filter((s): s is EditorialScope & { dealHuntSlug: string } => s.dealHuntSlug !== null)
    .map(s => ({
      id: s.id,
      label: s.label,
      short: s.short,
      dealHuntSlug: s.dealHuntSlug,
      hubs: s.hubs,
    }))
}

/**
 * The tabs of the compiled-in tree — what the header renders when a page's common data
 * carries none (the static fixture data source, a page built before the field existed).
 * The live tabs come through `DSCommonData.editorialTabs`, set by `ds/wp.ts` from the
 * loaded tree on every page.
 */
export const FALLBACK_HEADER_TABS: readonly HeaderTab[] = headerTabs(FALLBACK_TREE)

/**
 * The tabs a page's common data carries, else the compiled-in ones. An EMPTY list counts as
 * none: a WordPress tree whose scopes all lack `dealhunt_slug` (the dev install, 2026-10-05)
 * would otherwise strip Earbuds / Headphones / Speakers from the header and the phone drawer
 * on every server-rendered page — and only there, so the header changed between the home's
 * ISR snapshot and a Community page. A header with no category tab is never what WordPress
 * means; a scope it wants gone it leaves out of the tree.
 */
export function headerTabsOrFallback(
  tabs: readonly HeaderTab[] | null | undefined
): readonly HeaderTab[] {
  return tabs && tabs.length > 0 ? tabs : FALLBACK_HEADER_TABS
}

export interface EditorialHubConfig {
  id: string
  label: string
  /** WP `category` term slug. */
  categorySlug: string
  /** Absolute path on soundguys.com — these hubs are WP pages, not app routes. */
  path: string
  /** Count noun for a hub tile: "184 guides", "271 matchups". */
  noun: string
}

/**
 * The Coverage column of a category panel: each editorial hub, scoped to the tab.
 *
 * Sourced from the editorial taxonomy so a row and the page it opens can't disagree.
 * Trimmed to the four the header design shows — Guides & How-to's and News have their own
 * coverage pages but don't earn a header row. A tab lists only the ones its scope is on
 * (`coverageHubsFor`).
 */
export const COVERAGE_HUBS = EDITORIAL_HUBS.filter(hub => hub.id !== 'guides' && hub.id !== 'news')

/** The Coverage rows of one tab: the header's hubs the tab's scope is on. */
export function coverageHubsFor(tab: Pick<HeaderTab, 'hubs'>): readonly EditorialHub[] {
  return COVERAGE_HUBS.filter(hub => !tab.hubs || tab.hubs.includes(hub.id))
}

/**
 * A hub as an unscoped tile is its `EDITORIAL_HUBS` declaration (id, label,
 * categorySlug — never re-declared, so a hub can't carry two names) plus what only a
 * tile needs: the count noun. It links the hub's root page (`hubRootHref`): `/best/`,
 * `/guides/` — NOT `/best-products/`, which 404s.
 */
function tileFromHub(id: string, noun: string): EditorialHubConfig {
  const hub = EDITORIAL_HUBS.find(h => h.id === id)
  if (!hub) {
    throw new Error(`Unknown editorial hub: ${id}`)
  }
  return {
    id: hub.id,
    label: hub.label,
    categorySlug: hub.categorySlug,
    path: hubRootHref(hub, true),
    noun,
  }
}

/**
 * Every unscoped editorial hub, in the mobile frame's tile order. Each total is the
 * linked PAGE's own number (`fetchHubTotals`). Every tile is an `EDITORIAL_HUBS` hub.
 */
export const EDITORIAL_HUB_TILES: readonly EditorialHubConfig[] = [
  tileFromHub('reviews', 'articles'),
  tileFromHub('best', 'guides'),
  tileFromHub('versus', 'matchups'),
  tileFromHub('guides', "how-to's"),
  tileFromHub('news', 'stories'),
  tileFromHub('opinions', 'features'),
]

function hubTile(id: string): EditorialHubConfig {
  const tile = EDITORIAL_HUB_TILES.find(t => t.id === id)
  if (!tile) {
    throw new Error(`Unknown editorial hub tile: ${id}`)
  }
  return tile
}

/** The three story columns of the desktop Editorial panel, in design order —
 *  a subset of the tiles above so the two surfaces can never disagree on a hub. */
export const EDITORIAL_COLUMNS: readonly EditorialHubConfig[] = [
  hubTile('news'),
  hubTile('opinions'),
  hubTile('reviews'),
]

/** Stories per editorial column, and per category panel. */
export const EDITORIAL_STORIES_PER_COLUMN = 3
export const CATEGORY_STORIES = 2

/** The catalogue exit line, identical in the desktop panel and the drawer. */
export function viewAllLabel(productTotal: number | null, short: string): string {
  return productTotal
    ? `View all ${productTotal.toLocaleString('en-US')} ${short} →`
    : `View all ${short} →`
}

/** Where the client fetches the assembled document. */
export const HEADER_NAV_ENDPOINT = '/api/header-nav'
